import * as React from 'react';
import { type Category } from '@/types';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '../ui/table';
import { RiDeleteBinLine, RiEditLine } from 'react-icons/ri';
import { Button } from '../atoms/button';
import { deleteCategory } from '@/web/lib/services';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { categoriesQueryOptions } from '@/web/lib/queryOptions';
import { useNavigate, useParams } from '@tanstack/react-router';

export interface CategoriesListProps {
  category: Category | null;
  onAddCategory: (parentCategory: Category | null) => void;
  onEditCategory: (category: Category | null) => () => void;
}

export function CategoriesList(props: CategoriesListProps) {
  const navigate = useNavigate();
  const params = useParams({ from: '/categories/$categoryId' });
  const queryClient = useQueryClient();
  if (!props.category) {
    return null;
  }

  const handleAddCategory = () => {
    props.onAddCategory(props.category);
  }

  const deleteCategoryMutation = useMutation({
    mutationFn: deleteCategory,
    onSuccess: () => {
      queryClient.invalidateQueries(categoriesQueryOptions(params.categoryId));
      navigate({ to: `/categories` });
    },
  });

  const handleDeleteCategory = () => {
    if (props.category) {
      deleteCategoryMutation.mutate(props.category.uid);
    }
  }

  const showMappings = (category) => () => {
    navigate({ to: `/categories/mappings/${category.id}` })
  }

  const children = props.category.children || [];

  return (<div className="categories-list">
    <div className="flex flex-row">
      <div className="flex-1">
        <h1 className="text-4xl font-bold">{props.category.name}</h1>
      </div>
      <div className="flex flex-row w-[150px] justify-end gap-2">
        <Button variant="outline" onClick={props.onEditCategory(props.category)}><RiEditLine /></Button>
        <Button onClick={handleAddCategory}>Add Category</Button>
      </div>
    </div>
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Slug</TableHead>
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {children.map((category: any) => (
          <TableRow key={category.id}>
            <TableCell>{category.name}</TableCell>
            <TableCell>{category.id}</TableCell>
            <TableCell>
              <Button onClick={props.onEditCategory(category)}>Edit</Button>
              <Button className="ml-2" onClick={showMappings(category)}>Mappings</Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>)
}
